import { CSSProperties, FC, ReactNode } from 'react';
import { DefinitionTooltip, SkeletonText } from '@carbon/react';
import { teal, blue, green, red, purple, gray } from '@carbon/colors';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import {
  Money,
  ChartLineData,
  CheckmarkFilled,
  ChartColumn,
  ArrowUp,
  InProgress,
  WarningAltFilled,
} from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import {
  formatCost,
  formatSnapshotAt,
  unpricedMessage,
  inProgressCount,
  successRate,
} from './format';
import type {
  AgentCostSummary,
  AgentCostSeries,
  AgentCostTotals,
  UnpricedModel,
} from './types';
import styles from './kpi-cards.module.scss';

const formatInt = (value: number): string =>
  formatNumber({ value, minPrecision: 0, includeCommas: true });

// Tiny axis-less area sparkline used inside a KPI card.
const AreaSparkline: FC<{ data: number[]; color: string }> = ({
  data,
  color,
}) => (
  <Chart
    highcharts={Highcharts}
    options={{
      credits: { enabled: false },
      // No export/context-menu hamburger on the tiny KPI sparklines.
      exporting: { enabled: false },
      chart: {
        type: 'area',
        height: 44,
        margin: [2, 0, 2, 0],
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      xAxis: { visible: false },
      yAxis: { visible: false },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        area: {
          lineWidth: 1.5,
          lineColor: color,
          marker: { enabled: false },
          states: { hover: { enabled: false } },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}55`],
              [1, `${color}05`],
            ],
          },
        },
      },
      series: [{ type: 'area', data }] as Highcharts.SeriesOptionsType[],
    }}
  />
);

interface MetricCardProps {
  accent: string;
  icon: ReactNode;
  label: string;
  value: string;
  delta?: ReactNode;
  visual?: ReactNode;
  index: number;
  // Optional plain-text explainer shown as a tooltip on the card label.
  hint?: string;
  // Small affordance rendered next to the label (e.g. an unpriced-spend warning).
  badge?: ReactNode;
  // Quiet caption under the value (e.g. the cache "as of" time).
  footnote?: ReactNode;
}

// `columns` sets how many cards sit in a row at full width (default 5, matching
// the overview page). Pass the actual card count so the grid fills the row
// instead of leaving empty tracks on the right.
export const MetricGrid: FC<{ children: ReactNode; columns?: number }> = ({
  children,
  columns,
}) => (
  <div
    className={styles.grid}
    style={columns ? ({ '--stat-cols': columns } as CSSProperties) : undefined}
  >
    {children}
  </div>
);

export const MetricCard: FC<MetricCardProps> = ({
  accent,
  icon,
  label,
  value,
  delta,
  visual,
  index,
  hint,
  badge,
  footnote,
}) => (
  <div
    className={styles.card}
    style={
      {
        // accent rail color + staggered entrance
        '--accent': accent,
        'animationDelay': `${index * 70}ms`,
      } as CSSProperties
    }
  >
    <div className={styles.cardHead}>
      <span className={styles.cardLabel}>
        {hint ? (
          <DefinitionTooltip definition={hint} openOnHover>
            {label}
          </DefinitionTooltip>
        ) : (
          label
        )}
        {badge}
      </span>
      <span className={styles.cardIcon}>{icon}</span>
    </div>
    <div className={styles.cardValue}>{value}</div>
    {delta && <div className={styles.cardDelta}>{delta}</div>}
    {footnote && <div className={styles.cardFootnote}>{footnote}</div>}
    {visual && <div className={styles.cardVisual}>{visual}</div>}
  </div>
);

// Warning affordance on the Total Spend tile: some runs used models missing from
// the price list (counted as $0), so the headline total understates real spend.
const UnpricedBadge: FC<{ models: UnpricedModel[] }> = ({ models }) => (
  <DefinitionTooltip
    definition={unpricedMessage({ models, understates: true })}
    openOnHover
  >
    <WarningAltFilled
      size={16}
      className={styles.unpricedBadge}
      aria-label="Some models are unpriced"
    />
  </DefinitionTooltip>
);

export const STAT_COLORS = {
  cost: teal[60], // #007d79
  runs: blue[60], // #0f62fe
  success: green[50], // #24a148 — same token the SCSS uses via $support-success
  failed: red[60], // #da1e28 — same token the SCSS uses via $support-error
  inProgress: purple[60], // #8a3ffc
  neutral: gray[60], // #6f6f6f
};

const COLORS = STAT_COLORS;

// Loading placeholder that mirrors the KPI strip (5 cards, same grid + card
// chrome) so the page doesn't shift when the real metrics arrive.
export const KpiCardsSkeleton: FC = () => (
  <MetricGrid>
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className={styles.card}
        style={
          {
            '--accent': STAT_COLORS.neutral,
            'animationDelay': `${i * 70}ms`,
          } as CSSProperties
        }
      >
        <div className={styles.cardHead}>
          <SkeletonText width="45%" />
        </div>
        <SkeletonText heading width="65%" />
        <SkeletonText width="35%" />
      </div>
    ))}
  </MetricGrid>
);

export const KpiCards: FC<{
  totals: AgentCostTotals;
  agents: AgentCostSummary[];
  series: AgentCostSeries;
  // ISO time the totals were computed; drives the tile's "as of" stamp.
  generatedAt?: string;
  // Models with no price-list entry, listed in the unpriced-spend warning.
  unpricedModels?: UnpricedModel[];
}> = ({ totals, agents, series, generatedAt, unpricedModels = [] }) => {
  const totalSuccess = agents.reduce((s, a) => s + a.totalSuccess, 0);
  const totalFailed = agents.reduce((s, a) => s + a.totalFailed, 0);
  // Roll the strip's run counts into one shape so the In Progress + Success Rate
  // helpers (./format) compute against the same numbers the summary table does.
  const runCounts = {
    totalRuns: totals.totalRuns,
    totalSuccess,
    totalFailed,
  };
  // Rate is over *completed* runs (succeeded + failed). totals.totalRuns also
  // counts in-flight (pending/processing) runs, which would deflate the rate.
  const completedRuns = totalSuccess + totalFailed;
  const successRatePct = successRate(runCounts);
  const failureRate =
    completedRuns > 0 ? (totalFailed / completedRuns) * 100 : 0;
  const avgCostPerRun =
    totals.totalRuns > 0 ? totals.totalCost / totals.totalRuns : 0;
  // In-progress = runs counted in Total Runs that haven't completed (pending /
  // processing). Derived so the strip reconciles by construction:
  // Total Runs = Success + Failed + In Progress.
  const inProgress = inProgressCount(runCounts);
  const inProgressPct =
    totals.totalRuns > 0 ? (inProgress / totals.totalRuns) * 100 : 0;

  const sumPerDay = (s: AgentCostSeries['costByAgent']): number[] =>
    series.dates.map((_, i) =>
      s.reduce((sum, ser) => sum + (ser.data[i] ?? 0), 0)
    );
  const dailyCost = sumPerDay(series.costByAgent);
  const dailyRuns = sumPerDay(series.runsByStatus);
  // Daily pending/processing counts, for the In Progress sparkline.
  const dailyInProgress = series.dates.map((_, i) =>
    series.runsByStatus
      .filter((s) => s.name === 'processing' || s.name === 'pending')
      .reduce((sum, s) => sum + (s.data[i] ?? 0), 0)
  );

  return (
    <MetricGrid>
      <MetricCard
        index={0}
        accent={COLORS.cost}
        icon={<Money size={20} />}
        label="Total Spend (est.)"
        hint="Estimated cost of successful, priced agent runs only."
        value={formatCost(totals.totalCost)}
        badge={
          unpricedModels.length > 0 ? (
            <UnpricedBadge models={unpricedModels} />
          ) : undefined
        }
        footnote={
          generatedAt
            ? `Updated as of ${formatSnapshotAt(generatedAt)}`
            : undefined
        }
        delta={
          <span className={styles.deltaUp}>
            <ArrowUp size={14} />
            {formatCost(totals.costLast24h)} last 24h
          </span>
        }
        visual={
          dailyCost.length > 1 && (
            <AreaSparkline data={dailyCost} color={COLORS.cost} />
          )
        }
      />

      <MetricCard
        index={1}
        accent={COLORS.runs}
        icon={<ChartLineData size={20} />}
        label="Total Runs"
        hint="All agent runs in range. Total Runs = Success + Failed + In Progress (in-progress = pending/processing runs that haven't completed yet)."
        value={formatInt(totals.totalRuns)}
        delta={
          <span className={styles.deltaUp}>
            <ArrowUp size={14} />
            {formatInt(totals.runsLast24h)} last 24h
          </span>
        }
        visual={
          dailyRuns.length > 1 && (
            <AreaSparkline data={dailyRuns} color={COLORS.runs} />
          )
        }
      />

      <MetricCard
        index={2}
        accent={COLORS.inProgress}
        icon={<InProgress size={20} />}
        label="In Progress"
        hint="Runs counted in Total Runs that haven't completed yet (pending or processing). They're excluded from Success Rate, which is over completed runs only."
        value={formatInt(inProgress)}
        delta={
          <span className={styles.deltaMuted}>
            {formatNumber({
              value: inProgressPct,
              minPrecision: 1,
              maxPrecision: 1,
            })}
            % of total runs
          </span>
        }
        visual={
          dailyInProgress.length > 1 && (
            <AreaSparkline data={dailyInProgress} color={COLORS.inProgress} />
          )
        }
      />

      <MetricCard
        index={3}
        accent={COLORS.success}
        icon={<CheckmarkFilled size={20} />}
        label="Success Rate"
        hint="Share of completed runs that succeeded (succeeded ÷ (succeeded + failed)). In-progress runs are excluded."
        value={`${formatNumber({
          value: successRatePct,
          minPrecision: 1,
          maxPrecision: 1,
        })}%`}
        delta={
          <span className={styles.deltaMuted}>
            {formatInt(totalSuccess)} ok · {formatInt(totalFailed)} failed
          </span>
        }
        visual={
          <div className={styles.healthBar}>
            <span
              className={styles.healthSuccess}
              style={{ width: `${successRatePct}%` }}
            />
            <span
              className={styles.healthFailed}
              style={{ width: `${failureRate}%` }}
            />
          </div>
        }
      />

      <MetricCard
        index={4}
        accent={COLORS.neutral}
        icon={<ChartColumn size={20} />}
        label="Avg Cost / Run"
        value={formatNumber({
          value: avgCostPerRun,
          minPrecision: 4,
          maxPrecision: 4,
          style: 'currency',
          includeCommas: true,
        })}
        delta={
          <span className={styles.deltaMuted}>
            across {agents.length} agent{agents.length === 1 ? '' : 's'}
          </span>
        }
      />
    </MetricGrid>
  );
};
